import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { array, bool, func, number, object, string } from 'prop-types';

import { propTypes } from '../../../util/types';
import { ListingCard, IconSpinner, H2 } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';

import css from './SearchResultsPanel.module.css';

const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    onLoadMore,
    searchInProgress,
  } = props;

  const observerTarget = useRef(null);
  const isLoadingRef = useRef(false);
  const containerRef = useRef(null);

  const classes = classNames(rootClassName || css.root, className);
  
  // Check if there are more pages to load
  const hasMorePages =
    pagination &&
    typeof pagination.page === 'number' &&
    typeof pagination.totalPages === 'number' &&
    pagination.page < pagination.totalPages;
  const shouldShowLoader = hasMorePages && searchInProgress;
  const hasNoMoreResults =
    pagination &&
    typeof pagination.page === 'number' &&
    typeof pagination.totalPages === 'number' &&
    pagination.page >= pagination.totalPages &&
    listings.length > 0;

  // Update loading ref when searchInProgress changes
  useEffect(() => {
    isLoadingRef.current = searchInProgress;
  }, [searchInProgress]);

  // Infinite scroll using Intersection Observer
  // Trigger at 80-90% of content height
  useEffect(() => {
    if (!onLoadMore || !hasMorePages) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        // Only trigger if element is intersecting and we're not already loading
        if (entry.isIntersecting && !isLoadingRef.current) {
          isLoadingRef.current = true;
          onLoadMore();
        }
      },
      {
        // Trigger when trigger element enters viewport
        // rootMargin makes it trigger earlier (when user is ~200px from bottom)
        rootMargin: '200px',
        threshold: 0,
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [onLoadMore, hasMorePages, pagination?.page, pagination?.totalPages]);

  // Always show grid view - no carousels
  return (
    <div ref={containerRef} className={classes}>
      <div className={css.gridContainer}>
        {listings.map(listing => (
          <ListingCard
            className={css.listingCard}
            key={listing.id.uuid}
            listing={listing}
            setActiveListing={setActiveListing}
          />
        ))}
      </div>
      {hasMorePages ? (
        <div ref={observerTarget} className={css.loadMoreTrigger}>
          {shouldShowLoader && (
            <div className={css.loadingIndicator}>
              <IconSpinner rootClassName={css.spinner} />
              <FormattedMessage id="SearchPage.loadingMore" defaultMessage="Loading more..." />
            </div>
          )}
        </div>
      ) : null}
      {hasNoMoreResults && (
        <div className={css.noMoreResults}>
          <FormattedMessage id="SearchPage.noMoreResults" defaultMessage="Inga fler träffar" />
        </div>
      )}
      {props.children}
    </div>
  );
};

SearchResultsPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listings: [],
  pagination: null,
  search: null,
  setActiveListing: null,
  isMapVariant: true,
  listingTypeParam: null,
};

SearchResultsPanel.propTypes = {
  className: string,
  rootClassName: string,
  listings: array,
  pagination: object,
  search: object,
  setActiveListing: func,
  isMapVariant: bool,
  listingTypeParam: string,
  onLoadMore: func,
  searchInProgress: bool,
};

export default SearchResultsPanel;
